/**
 * FE-C1: Contract-first path builder + optional api() binding.
 * Paths come from generated CONTRACT_BY_ID (manifest SoT).
 */

import { CONTRACT_BY_ID } from "./contract.generated.js";

/**
 * @typedef {object} ContractOperation
 * @property {string} operation_id
 * @property {string} method
 * @property {string} path
 * @property {string} public_path
 * @property {string} auth
 * @property {string} path_template
 * @property {string} [domain]
 */

/** @type {((path: string, opts?: object) => Promise<unknown>) | null} */
let _contractApi = null;

/**
 * @param {(path: string, opts?: object) => Promise<unknown>} apiFn
 */
export function bindContractApi(apiFn) {
  _contractApi = apiFn;
}

/**
 * @param {string} operationId
 * @returns {ContractOperation}
 */
export function getContractOperation(operationId) {
  const op = CONTRACT_BY_ID[operationId];
  if (!op) {
    throw new Error(`Unknown operation_id: ${operationId}`);
  }
  return op;
}

/**
 * Build a browser-relative API path (no apiBase prefix) for shell `api()`.
 *
 * @param {string} operationId
 * @param {{
 *   params?: Record<string, string | number>,
 *   pathParams?: Array<string | number>,
 *   query?: Record<string, string | number | boolean | null | undefined>,
 * }} [options]
 * @returns {string}
 */
export function buildContractPath(operationId, options = {}) {
  const op = getContractOperation(operationId);
  const params = options.params || {};
  const pathParams = options.pathParams;
  let path = op.path_template || op.path;
  if (Array.isArray(pathParams) && pathParams.length) {
    let idx = 0;
    path = path.replace(/\{[^}]+\}/g, () => {
      if (idx >= pathParams.length) {
        throw new Error(`Missing pathParams[${idx}] for ${operationId}`);
      }
      return encodeURIComponent(String(pathParams[idx++]));
    });
  } else {
    const namedKeys = Object.keys(params);
    path = path.replace(/\{([^}]+)\}/g, (_, name) => {
      if (name in params) {
        return encodeURIComponent(String(params[name]));
      }
      /* OpenAPI collapse often yields `{param}` while callers still pass
         `{ device_id }` / `{ username }`. Accept the sole named value. */
      if (name === "param" && namedKeys.length === 1) {
        return encodeURIComponent(String(params[namedKeys[0]]));
      }
      throw new Error(`Missing path param "${name}" for ${operationId}`);
    });
  }
  const query = options.query || {};
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v == null || v === "") continue;
    qs.set(k, String(v));
  }
  const q = qs.toString();
  if (q) path += (path.includes("?") ? "&" : "?") + q;
  return path;
}

/**
 * @param {string} operationId
 * @param {object} [opts]
 * @returns {Promise<unknown>}
 */
export async function apiByOperation(operationId, opts = {}) {
  if (!_contractApi) throw new Error("apiByOperation: api not bound");
  const op = getContractOperation(operationId);
  const method = String(opts.method || op.method || "GET").toUpperCase();
  const path = buildContractPath(operationId, opts);
  return _contractApi(path, { ...opts, method });
}
